import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppDateField } from '../../components/AppDateField';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { PERMISSIONS } from '../../constants/permissions';
import { routes } from '../../constants/routes';
import { layout } from '../../constants/layout';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createHoliday,
  createWeekOffs,
  deleteHoliday,
  getCalendarMonthNotes,
  getMonthlyCalendar,
  getOfficeCalendarMonthlyLeaveAccrual,
  saveCalendarMonthNotes,
  updateHoliday,
} from '../../services/officeCalendarService';
import {
  calendarLegendColor,
  calendarLegendLabel,
  calendarLegendStatus,
  CALENDAR_LEGEND_STATUSES,
  defaultEditableCalendarDate,
  formatHolidayDateAndDay,
  isEditableCalendarDate,
  isWeekOffHolidayType,
  monthHasEditableCalendarDates,
  WEEK_OFF_NAME,
} from '../../utils/officeCalendar';
import { parseCalendarDateInput, todayBusinessCalendarDate } from '../../utils/dateTime';
import { monthExpectedWorkingDays } from '../../utils/monthWorkMetrics';
import { normalizeApiError } from '../../api/apiError';
import { confirmSoftDelete } from '../../utils/confirmSoftDelete';
import { toFriendlyErrorMessage } from '../../utils/friendlyError';
import type {
  CalendarDay,
  HolidayFormInput,
  HolidayType,
  MonthlyCalendarData,
} from '../../types/officeCalendar';
import { runOperation } from '../../utils/operationFeedback';
import { OperationFeedbackHost } from '../../components/OperationFeedbackHost';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const isWeb = (Platform.OS as string) === 'web';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const EMPTY_FORM: HolidayFormInput = {
  holidayName: '',
  holidayDate: '',
  holidayType: 'ORGANIZATION',
  description: '',
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function padTwo(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDayCount(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  return String(rounded);
}

function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return `${padTwo(d)}-${MONTH_NAMES[m - 1]?.slice(0, 3) ?? ''}-${y}`;
}

function WebEditPencilIcon({ color }: { color: string }) {
  return (
    <View
      accessibilityElementsHidden
      pointerEvents="none"
      style={{ width: 18, height: 18 }}
    >
      {React.createElement(
        'svg',
        {
          width: 18,
          height: 18,
          viewBox: '0 0 24 24',
          fill: 'none',
          stroke: color,
          strokeWidth: 2,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          'aria-hidden': true,
        },
        React.createElement('path', {
          d: 'M12 20h9',
        }),
        React.createElement('path', {
          d: 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z',
        }),
      )}
    </View>
  );
}

function holidayTypeLabel(type: HolidayType | null): string {
  if (isWeekOffHolidayType(type)) return 'Weekly Off';
  return 'Holiday';
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

function LegendDot({ color }: { color: string }) {
  return <View style={[legendStyles.dot, { backgroundColor: color }]} />;
}

const legendStyles = StyleSheet.create({
  dot: { width: 10, height: 10, borderRadius: 5 },
});

// ─────────────────────────────────────────────────────────────
// Main screen
// ─────────────────────────────────────────────────────────────

export function OfficeCalendarScreen() {
  const palette = useAppColors();
  const { isCompact } = useResponsiveLayout();
  const auth = useAuth();
  const navigation = useNavigation();
  const route = useRoute();
  const canManage = auth.hasPermission(PERMISSIONS.CALENDAR_MANAGE);
  const routeParams = (route.params || {}) as {
    holidayCalendarId?: number;
    calendarName?: string;
  };
  const holidayCalendarId = routeParams.holidayCalendarId ?? null;
  const calendarTitle = routeParams.calendarName?.trim() || 'Office Calendar';

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [calendarData, setCalendarData] = useState<MonthlyCalendarData | null>(null);
  const [monthlyLeaveAccrual, setMonthlyLeaveAccrual] = useState(0);
  const [monthNotes, setMonthNotes] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);

  // Holiday form modal state
  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editHolidayId, setEditHolidayId] = useState<number | null>(null);
  const [form, setForm] = useState<HolidayFormInput>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSaving, setFormSaving] = useState(false);

  const [weekOffVisible, setWeekOffVisible] = useState(false);
  const [weekOffMode, setWeekOffMode] = useState<'create' | 'edit'>('create');
  const [weekOffEditId, setWeekOffEditId] = useState<number | null>(null);
  const [weekOffDates, setWeekOffDates] = useState<string[]>([]);
  const [weekOffError, setWeekOffError] = useState<string | null>(null);
  const [weekOffSaving, setWeekOffSaving] = useState(false);

  // ── Data loading ──────────────────────────────────────────

  const loadCalendar = useCallback(async (y: number, m: number) => {
    setLoading(true);
    setError(null);
    try {
      const [data, leaveAccrual, notes] = await Promise.all([
        getMonthlyCalendar(y, m, holidayCalendarId),
        getOfficeCalendarMonthlyLeaveAccrual(y),
        getCalendarMonthNotes(y, m, holidayCalendarId).catch(() => null),
      ]);
      const loadedNotes = notes ?? data.notes ?? '';
      setCalendarData(data);
      setMonthlyLeaveAccrual(leaveAccrual);
      setMonthNotes(loadedNotes);
      setNotesDraft(loadedNotes);
      setNotesEditing(false);
      setNotesError(null);
    } catch (err) {
      setError(toFriendlyErrorMessage(err, 'Failed to load calendar. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [holidayCalendarId]);

  useEffect(() => {
    loadCalendar(year, month);
    setSelectedDay(null);
  }, [year, month, loadCalendar, holidayCalendarId]);

  // ── Month navigation ──────────────────────────────────────

  function prevMonth() {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else setMonth((m) => m - 1);
  }

  function nextMonth() {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else setMonth((m) => m + 1);
  }

  // ── Calendar grid data ────────────────────────────────────

  const gridDays = useMemo(() => {
    if (!calendarData) return [];
    // dayOfWeek 1=Mon..7=Sun; pad start with nulls
    const firstDay = calendarData.calendarDays[0];
    const offset = firstDay ? firstDay.dayOfWeek - 1 : 0;
    const padded: (CalendarDay | null)[] = Array(offset).fill(null);
    return padded.concat(calendarData.calendarDays);
  }, [calendarData]);

  const todayDate = todayBusinessCalendarDate();
  const todayPickerMin = parseCalendarDateInput(todayDate) ?? undefined;
  const canAddInViewedMonth = monthHasEditableCalendarDates(year, month, todayDate);
  const selectedDateEditable = selectedDay
    ? isEditableCalendarDate(selectedDay.date, todayDate)
    : false;

  // ── Holiday CRUD ──────────────────────────────────────────

  function openCreateHolidayForm() {
    const holidayDate = defaultEditableCalendarDate(
      year,
      month,
      selectedDay?.date,
      todayDate,
    );
    if (!holidayDate) return;
    setFormMode('create');
    setEditHolidayId(null);
    setForm({
      ...EMPTY_FORM,
      holidayDate,
    });
    setFormError(null);
    setFormVisible(true);
  }

  function openCreateWeekOffForm() {
    const initial =
      selectedDay &&
      !selectedDay.holidayId &&
      isEditableCalendarDate(selectedDay.date, todayDate)
        ? [selectedDay.date]
        : [];
    setWeekOffMode('create');
    setWeekOffEditId(null);
    setWeekOffDates(initial);
    setWeekOffError(null);
    setWeekOffVisible(true);
  }

  function openEditForm(day: CalendarDay) {
    if (!day.holidayId) return;
    if (!isEditableCalendarDate(day.date, todayDate)) return;
    if (isWeekOffHolidayType(day.holidayType)) {
      setWeekOffMode('edit');
      setWeekOffEditId(day.holidayId);
      setWeekOffDates([day.date]);
      setWeekOffError(null);
      setWeekOffVisible(true);
      return;
    }
    setFormMode('edit');
    setEditHolidayId(day.holidayId);
    setForm({
      holidayName: day.holidayName ?? '',
      holidayDate: day.date,
      holidayType: day.holidayType ?? 'ORGANIZATION',
      description: day.holidayDescription ?? '',
    });
    setFormError(null);
    setFormVisible(true);
  }

  function toggleWeekOffDate(date: string) {
    if (!isEditableCalendarDate(date, todayDate)) return;
    if (weekOffMode === 'edit') {
      setWeekOffDates([date]);
      return;
    }
    setWeekOffDates((current) =>
      current.includes(date)
        ? current.filter((item) => item !== date)
        : [...current, date].sort(),
    );
  }

  async function saveForm() {
    if (!form.holidayDate) {
      setFormError('Date is required.');
      return;
    }
    if (!isEditableCalendarDate(form.holidayDate, todayDate)) {
      setFormError(
        'Holiday and Weekly Off can only be added or edited for today or a future date.',
      );
      return;
    }
    if (!form.holidayName.trim()) {
      setFormError('Holiday name is required.');
      return;
    }
    setFormSaving(true);
    setFormError(null);
    try {
      const payload: HolidayFormInput = {
        ...form,
        holidayName: form.holidayName.trim(),
        holidayType: isWeekOffHolidayType(form.holidayType)
          ? 'ORGANIZATION'
          : form.holidayType,
      };
      if (formMode === 'create') {
        await runOperation('add', () => createHoliday(payload, holidayCalendarId));
      } else if (editHolidayId) {
        await runOperation('update', () => updateHoliday(editHolidayId, payload));
      }
      setFormVisible(false);
      await loadCalendar(year, month);
      setSelectedDay(null);
    } catch (caught) {
      setFormError(
        normalizeApiError(caught).message || 'Failed to save. Please try again.',
      );
    } finally {
      setFormSaving(false);
    }
  }

  async function applyWeekOffs() {
    if (!weekOffDates.length) {
      setWeekOffError('Select at least one date in this month.');
      return;
    }
    if (weekOffDates.some((date) => !isEditableCalendarDate(date, todayDate))) {
      setWeekOffError(
        'Holiday and Weekly Off can only be added or edited for today or a future date.',
      );
      return;
    }
    setWeekOffSaving(true);
    setWeekOffError(null);
    try {
      if (weekOffMode === 'edit' && weekOffEditId) {
        await runOperation('update', () => updateHoliday(weekOffEditId, {
          holidayName: WEEK_OFF_NAME,
          holidayDate: weekOffDates[0],
          holidayType: 'WEEK_OFF',
          description: '',
        }));
      } else {
        await runOperation('add', () => createWeekOffs(weekOffDates, year, month, holidayCalendarId));
      }
      setWeekOffVisible(false);
      await loadCalendar(year, month);
      setSelectedDay(null);
    } catch (caught) {
      setWeekOffError(
        normalizeApiError(caught).message || 'Failed to apply Weekly Off. Please try again.',
      );
    } finally {
      setWeekOffSaving(false);
    }
  }

  function confirmDelete(day: CalendarDay) {
    if (!day.holidayId) return;
    const kindLabel = isWeekOffHolidayType(day.holidayType) ? 'weekly off' : 'holiday';
    confirmSoftDelete({
      entityLabel: kindLabel,
      recordName: isWeekOffHolidayType(day.holidayType)
        ? formatDisplayDate(day.date)
        : day.holidayName || formatDisplayDate(day.date),
      onConfirm: async () => {
        try {
          await runOperation('delete', () => deleteHoliday(day.holidayId!));
        } catch {
          // runOperation already showed the "Delete failed" popup.
          return;
        }
        setSelectedDay(null);
        await loadCalendar(year, month);
      },
    });
  }

  // ── Colors ────────────────────────────────────────────────

  const colors = useMemo(() => ({
    working: palette.success ?? '#22C55E',
    holiday: palette.error ?? '#EF4444',
    weekend: palette.textMuted ?? '#94A3B8',
    today: palette.primary,
  }), [palette]);

  function statusColor(day: CalendarDay): string {
    return calendarLegendColor(day, colors);
  }

  // ─────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────

  const themed = useMemo(
    () =>
      StyleSheet.create({
        monthNav: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: spacing.lg,
          paddingVertical: spacing.md,
          backgroundColor: palette.surface,
          borderBottomWidth: 1,
          borderBottomColor: palette.border ?? '#E2E8F0',
        },
        monthNavBtn: {
          width: 36,
          height: 36,
          borderRadius: 18,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: palette.background,
        },
        monthNavArrow: {
          ...typography.body,
          color: palette.primary,
          fontWeight: '700' as const,
          fontSize: 18,
        },
        monthLabel: {
          ...typography.sectionTitle,
          color: palette.textPrimary,
          fontWeight: '700' as const,
        },
        weekHeader: {
          flexDirection: 'row',
          backgroundColor: palette.surface,
          borderBottomWidth: 1,
          borderBottomColor: palette.border ?? '#E2E8F0',
        },
        weekHeaderCell: {
          flex: 1,
          alignItems: 'center',
          paddingVertical: spacing.sm,
        },
        weekHeaderText: {
          ...typography.caption,
          color: palette.textMuted,
          fontWeight: '600' as const,
          fontSize: 11,
        },
        grid: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          backgroundColor: palette.surface,
        },
        cell: {
          width: `${100 / 7}%` as any,
          minHeight: 56,
          alignItems: 'center',
          justifyContent: 'flex-start',
          borderRightWidth: StyleSheet.hairlineWidth,
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderColor: palette.border ?? '#E2E8F0',
          paddingTop: 4,
          paddingHorizontal: 1,
          paddingBottom: 2,
        },
        dayCircle: {
          width: 32,
          height: 32,
          borderRadius: 16,
          alignItems: 'center',
          justifyContent: 'center',
        },
        dayNumber: {
          ...typography.caption,
          fontWeight: '700' as const,
          fontSize: 13,
        },
        holidayName: {
          ...typography.caption,
          fontSize: 8,
          fontWeight: '600' as const,
          textAlign: 'center',
          marginTop: 1,
          lineHeight: 10,
        },
        summaryRow: {
          flexDirection: 'row',
          justifyContent: 'space-around',
          backgroundColor: palette.surface,
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: palette.border ?? '#E2E8F0',
          paddingVertical: spacing.md,
        },
        summaryItem: { alignItems: 'center', gap: 2, flex: 1, paddingHorizontal: 4 },
        summaryValue: {
          ...typography.sectionTitle,
          color: palette.textPrimary,
          fontWeight: '700' as const,
        },
        summaryLabel: {
          ...typography.caption,
          color: palette.textMuted,
          fontSize: 11,
          textAlign: 'center' as const,
        },
        notesSection: {
          marginHorizontal: spacing.lg,
          marginTop: spacing.lg,
          gap: spacing.sm,
        },
        notesTitleRow: {
          flexDirection: 'row' as const,
          alignItems: 'center' as const,
          justifyContent: 'space-between' as const,
        },
        notesEditBtn: {
          width: 36,
          height: 36,
          alignItems: 'center' as const,
          justifyContent: 'center' as const,
          ...(isWeb ? { cursor: 'pointer' as const } : {}),
        },
        notesPencil: {
          width: 12,
          height: 16,
          borderWidth: 2,
          borderRadius: 2,
          transform: [{ rotate: '-20deg' }],
        },
        notesActions: {
          flexDirection: 'row' as const,
          gap: spacing.sm,
        },
        notesBody: {
          ...typography.caption,
          color: palette.textSecondary ?? palette.textMuted,
          lineHeight: 18,
        },
        holidaysSection: {
          marginHorizontal: spacing.lg,
          marginTop: spacing.lg,
          gap: spacing.sm,
        },
        holidaysSectionTitle: {
          ...typography.sectionTitle,
          color: palette.textPrimary,
          fontWeight: '700' as const,
        },
        holidayRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: spacing.md,
          paddingVertical: spacing.sm,
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: palette.border ?? '#E2E8F0',
        },
        holidayRowDate: {
          ...typography.caption,
          color: palette.textMuted,
          fontWeight: '600' as const,
        },
        notesInput: {
          minHeight: 96,
          marginBottom: 0,
        },
        holidayRowName: {
          ...typography.body,
          color: palette.textPrimary,
          fontWeight: '600' as const,
          flex: 1,
        },
        legend: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: spacing.md,
          paddingHorizontal: spacing.lg,
          paddingVertical: spacing.sm,
          backgroundColor: palette.surface,
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: palette.border ?? '#E2E8F0',
        },
        legendItem: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: spacing.xs,
        },
        legendText: {
          ...typography.caption,
          color: palette.textMuted,
        },
        detailCard: {
          margin: spacing.lg,
          padding: spacing.lg,
          borderRadius: 12,
          backgroundColor: palette.surface,
          gap: spacing.sm,
          // Shadow
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.06,
          shadowRadius: 4,
          elevation: 2,
        },
        detailHeader: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        },
        detailDateLabel: {
          ...typography.caption,
          color: palette.textMuted,
        },
        detailDate: {
          ...typography.sectionTitle,
          color: palette.textPrimary,
          fontWeight: '700' as const,
        },
        detailTypeBadge: {
          paddingHorizontal: spacing.sm,
          paddingVertical: 3,
          borderRadius: 6,
          alignSelf: 'flex-start',
        },
        detailTypeBadgeText: {
          ...typography.caption,
          fontWeight: '700' as const,
          fontSize: 11,
        },
        detailHolidayName: {
          ...typography.body,
          color: palette.textPrimary,
          fontWeight: '700' as const,
        },
        detailDescription: {
          ...typography.caption,
          color: palette.textSecondary ?? palette.textMuted,
        },
        detailActions: {
          flexDirection: 'row',
          gap: spacing.sm,
          marginTop: spacing.xs,
        },
        errorText: {
          ...typography.body,
          color: palette.error,
          textAlign: 'center',
          margin: spacing.lg,
        },
        // Modal
        overlay: {
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.45)',
          justifyContent: isWeb ? ('center' as const) : ('flex-end' as const),
          alignItems: isWeb ? ('center' as const) : ('stretch' as const),
          padding: isWeb ? spacing.xl : 0,
        },
        sheet: {
          backgroundColor: palette.surface,
          borderTopLeftRadius: isWeb ? 16 : 20,
          borderTopRightRadius: isWeb ? 16 : 20,
          borderBottomLeftRadius: isWeb ? 16 : 0,
          borderBottomRightRadius: isWeb ? 16 : 0,
          padding: spacing.xl,
          gap: spacing.md,
          maxHeight: '90%',
          width: isWeb ? '100%' : undefined,
          maxWidth: isWeb ? layout.webModalMaxWidth : undefined,
        },
        sheetTitle: {
          ...typography.sectionTitle,
          color: palette.textPrimary,
          fontWeight: '700' as const,
          marginBottom: spacing.xs,
        },
        inputLabel: {
          ...typography.caption,
          color: palette.textMuted,
          marginBottom: spacing.xs,
          fontWeight: '600' as const,
        },
        inputField: {
          borderWidth: 1,
          borderColor: palette.border ?? '#E2E8F0',
          borderRadius: 8,
          padding: spacing.md,
          ...typography.body,
          color: palette.textPrimary,
          backgroundColor: palette.background,
          marginBottom: spacing.sm,
        },
        formError: {
          ...typography.caption,
          color: palette.error,
          marginBottom: spacing.xs,
        },
        addHolidayBtn: {
          marginHorizontal: spacing.lg,
          marginTop: spacing.md,
          gap: spacing.sm,
        },
        weekOffHint: {
          ...typography.caption,
          color: palette.textMuted,
          marginBottom: spacing.sm,
        },
        weekOffWeekHeader: {
          flexDirection: 'row',
        },
        weekOffGrid: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          marginBottom: spacing.md,
        },
        weekOffCell: {
          width: `${100 / 7}%` as any,
          minHeight: 42,
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: 4,
        },
        weekOffDay: {
          width: 32,
          height: 32,
          borderRadius: 16,
          alignItems: 'center',
          justifyContent: 'center',
        },
        weekOffDayText: {
          ...typography.caption,
          fontWeight: '700' as const,
          fontSize: 13,
        },
        selectedDatesText: {
          ...typography.caption,
          color: palette.textPrimary,
          marginBottom: spacing.sm,
        },
      }),
    [palette, colors],
  );

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────

  function renderDayTypeBadge(day: CalendarDay) {
    const status = calendarLegendStatus(day);
    const textColor = colors[status];
    return (
      <View style={[themed.detailTypeBadge, { backgroundColor: `${textColor}22` }]}>
        <Text style={[themed.detailTypeBadgeText, { color: textColor }]}>
          {calendarLegendLabel(status)}
        </Text>
      </View>
    );
  }

  function renderSelectedDayDetail() {
    if (!selectedDay) return null;
    return (
      <View style={themed.detailCard}>
        <View style={themed.detailHeader}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={themed.detailDateLabel}>Selected Date</Text>
            <Text style={[themed.detailDate, { color: statusColor(selectedDay) }]}>
              {formatHolidayDateAndDay(
                selectedDay.date,
                selectedDay.dayName,
                selectedDay.displayDate,
              )}
            </Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            {renderDayTypeBadge(selectedDay)}
            {canManage && selectedDay.holidayId ? (
              <ConfigurationDeleteButton
                placement="inline"
                entityLabel={
                  isWeekOffHolidayType(selectedDay.holidayType)
                    ? 'week off'
                    : 'holiday'
                }
                onPress={() => confirmDelete(selectedDay)}
              />
            ) : null}
          </View>
        </View>
        {selectedDay.holidayName ? (
          <>
            <Text style={themed.detailHolidayName}>{selectedDay.holidayName}</Text>
            {selectedDay.holidayType ? (
              <Text style={themed.detailDescription}>
                {holidayTypeLabel(selectedDay.holidayType)}
              </Text>
            ) : null}
            {selectedDay.holidayDescription ? (
              <Text style={themed.detailDescription}>{selectedDay.holidayDescription}</Text>
            ) : null}
            {canManage && selectedDay.holidayId ? (
              <>
                {selectedDateEditable ? (
                  <View style={themed.detailActions}>
                    <View style={{ flex: 1 }}>
                      <AppButton
                        title="Edit"
                        variant="secondary"
                        onPress={() => openEditForm(selectedDay)}
                      />
                    </View>
                  </View>
                ) : (
                  <Text style={themed.detailDescription}>
                    Past dates cannot be edited.
                  </Text>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </View>
    );
  }

  function renderCalendarGrid() {
    const rows: (CalendarDay | null)[][] = [];
    for (let i = 0; i < gridDays.length; i += 7) {
      rows.push(gridDays.slice(i, i + 7));
    }
    return (
      <View style={themed.grid}>
        {gridDays.map((day, idx) => {
          if (!day) {
            return <View key={`empty-${idx}`} style={themed.cell} />;
          }
          const isSelected = selectedDay?.date === day.date;
          const legendColor = statusColor(day);
          return (
            <Pressable
              key={day.date}
              style={[
                themed.cell,
                {
                  backgroundColor: isSelected
                    ? `${legendColor}22`
                    : palette.surface,
                },
              ]}
              onPress={() => setSelectedDay(isSelected ? null : day)}
              accessibilityRole="button"
              accessibilityLabel={`${day.date}${day.holidayName ? ', ' + day.holidayName : ''}, ${calendarLegendLabel(calendarLegendStatus(day))}`}
              accessibilityState={{ selected: isSelected }}
            >
              <View
                style={[
                  themed.dayCircle,
                  day.isToday
                    ? { borderWidth: 2, borderColor: legendColor }
                    : null,
                ]}
              >
                <Text style={[themed.dayNumber, { color: legendColor }]}>
                  {Number(day.date.slice(8))}
                </Text>
              </View>
              {day.holidayName ? (
                <Text
                  style={[themed.holidayName, { color: legendColor }]}
                  numberOfLines={2}
                >
                  {day.holidayName}
                </Text>
              ) : null}
            </Pressable>
          );
        })}
      </View>
    );
  }

  function renderSummary() {
    if (!calendarData) return null;
    const { summary } = calendarData;
    const expectedWorkingDays = monthExpectedWorkingDays(
      summary.workingDays,
      monthlyLeaveAccrual,
    );
    return (
      <>
        <View style={themed.summaryRow}>
          <View style={themed.summaryItem}>
            <Text style={[themed.summaryValue, { color: colors.working }]}>
              {formatDayCount(summary.workingDays)}
            </Text>
            <Text style={themed.summaryLabel}>Working Days</Text>
          </View>
          <View style={themed.summaryItem}>
            <Text style={[themed.summaryValue, { color: colors.holiday }]}>
              {summary.holidays}
            </Text>
            <Text style={themed.summaryLabel}>Holidays</Text>
          </View>
          <View style={themed.summaryItem}>
            <Text style={[themed.summaryValue, { color: colors.weekend }]}>
              {summary.weekends}
            </Text>
            <Text style={themed.summaryLabel}>Weekly Off</Text>
          </View>
        </View>
        <View style={themed.summaryRow}>
          <View style={themed.summaryItem}>
            <Text style={[themed.summaryValue, { color: palette.primary }]}>
              {formatDayCount(expectedWorkingDays)}
            </Text>
            <Text style={themed.summaryLabel}>Expected Working Days</Text>
          </View>
          <View style={themed.summaryItem}>
            <Text style={themed.summaryValue}>
              {formatDayCount(monthlyLeaveAccrual)}
            </Text>
            <Text style={themed.summaryLabel}>Monthly Leave Accrual</Text>
          </View>
        </View>
      </>
    );
  }

  function renderNamedDaysList(title: string, days: CalendarDay[]) {
    if (!days.length) return null;
    return (
      <View style={themed.holidaysSection}>
        <Text style={themed.holidaysSectionTitle}>
          {title} ({days.length})
        </Text>
        <AppCard style={{ paddingHorizontal: spacing.md }}>
          {days.map((day) => (
            <Pressable
              key={day.date}
              style={themed.holidayRow}
              onPress={() => setSelectedDay(day)}
            >
              <View style={{ flex: 1, gap: 2 }}>
                <Text style={themed.holidayRowDate}>
                  {formatHolidayDateAndDay(day.date, day.dayName, day.displayDate)}
                </Text>
                <Text style={themed.holidayRowName} numberOfLines={2}>
                  {day.holidayName}
                </Text>
              </View>
            </Pressable>
          ))}
        </AppCard>
      </View>
    );
  }

  async function saveMonthNotes() {
    if (!canManage) return;
    setNotesSaving(true);
    setNotesError(null);
    try {
      const saved = await runOperation('update', () => saveCalendarMonthNotes(
        year,
        month,
        notesDraft,
        holidayCalendarId,
      ));
      setMonthNotes(saved);
      setNotesDraft(saved);
      setNotesEditing(false);
      setCalendarData((current) =>
        current ? { ...current, notes: saved } : current,
      );
    } catch (caught) {
      setNotesError(
        normalizeApiError(caught).message || 'Failed to save notes. Please try again.',
      );
    } finally {
      setNotesSaving(false);
    }
  }

  function startEditNotes() {
    if (!canManage || notesSaving) return;
    setNotesDraft(monthNotes);
    setNotesError(null);
    setNotesEditing(true);
  }

  function cancelEditNotes() {
    if (notesSaving) return;
    setNotesDraft(monthNotes);
    setNotesError(null);
    setNotesEditing(false);
  }

  function renderNotes() {
    if (!canManage && !monthNotes.trim()) return null;
    return (
      <View style={themed.notesSection}>
        <View style={themed.notesTitleRow}>
          <Text style={themed.holidaysSectionTitle}>Notes</Text>
          {canManage && !notesEditing ? (
            <Pressable
              style={themed.notesEditBtn}
              onPress={startEditNotes}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Edit notes"
              accessibilityHint="Opens the month notes editor"
            >
              {isWeb ? (
                <WebEditPencilIcon color={palette.primary} />
              ) : (
                <View
                  style={[themed.notesPencil, { borderColor: palette.primary }]}
                />
              )}
            </Pressable>
          ) : null}
        </View>
        <AppCard style={{ padding: spacing.md, gap: spacing.sm }}>
          {canManage && notesEditing ? (
            <>
              <TextInput
                style={[themed.inputField, themed.notesInput]}
                placeholder="Add notes for this month"
                placeholderTextColor={palette.textMuted}
                value={notesDraft}
                onChangeText={setNotesDraft}
                multiline
                textAlignVertical="top"
                maxLength={4000}
                editable={!notesSaving}
                accessibilityLabel="Month notes"
              />
              {notesError ? (
                <Text style={themed.formError}>{notesError}</Text>
              ) : null}
              <View style={themed.notesActions}>
                <View style={{ flex: 1 }}>
                  <AppButton
                    title="Save"
                    onPress={saveMonthNotes}
                    loading={notesSaving}
                    disabled={notesSaving}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <AppButton
                    title="Cancel"
                    variant="secondary"
                    onPress={cancelEditNotes}
                    disabled={notesSaving}
                  />
                </View>
              </View>
            </>
          ) : monthNotes.trim() ? (
            <Text style={themed.notesBody}>{monthNotes}</Text>
          ) : (
            <Text style={themed.notesBody}>No notes for this month.</Text>
          )}
        </AppCard>
      </View>
    );
  }

  function renderHolidaysList() {
    const days = calendarData?.calendarDays ?? [];
    const holidays = days.filter(
      (day) => day.dayType === 'HOLIDAY' && day.holidayName,
    );
    const weeklyOffs = days.filter(
      (day) => day.dayType === 'WEEKEND' && day.holidayName,
    );
    if (!holidays.length && !weeklyOffs.length) return null;
    return (
      <>
        {renderNamedDaysList('Holiday', holidays)}
        {renderNamedDaysList('Weekly Off', weeklyOffs)}
      </>
    );
  }

  function renderLegend() {
    return (
      <View style={themed.legend}>
        {CALENDAR_LEGEND_STATUSES.map((status) => (
          <View key={status} style={themed.legendItem}>
            <LegendDot color={colors[status]} />
            <Text style={themed.legendText}>{calendarLegendLabel(status)}</Text>
          </View>
        ))}
      </View>
    );
  }

  function renderHolidayForm() {
    return (
      <Modal
        visible={formVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setFormVisible(false)}
      >
        <KeyboardAvoidingView
          style={themed.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setFormVisible(false)}
          />
          <Pressable onPress={() => {}} style={themed.sheet}>
            <ScrollView
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="interactive"
              automaticallyAdjustKeyboardInsets
            >
              <Text style={themed.sheetTitle}>
                {formMode === 'create' ? 'Add Holiday' : 'Edit Holiday'}
              </Text>

              <View style={{ marginBottom: spacing.sm }}>
                <AppDateField
                  label="Date *"
                  value={form.holidayDate}
                  onChange={(holidayDate) => setForm((f) => ({ ...f, holidayDate }))}
                  placeholder="Select date"
                  minimumDate={todayPickerMin}
                />
              </View>

              <Text style={themed.inputLabel}>Holiday Name *</Text>
              <TextInput
                style={themed.inputField}
                placeholder="e.g. Independence Day"
                placeholderTextColor={palette.textMuted}
                value={form.holidayName}
                onChangeText={(v) => setForm((f) => ({ ...f, holidayName: v }))}
              />

              {formError ? (
                <Text style={themed.formError}>{formError}</Text>
              ) : null}

              <AppButton
                title="Submit"
                onPress={saveForm}
                loading={formSaving}
                disabled={formSaving || !isEditableCalendarDate(form.holidayDate, todayDate)}
              />
              <View style={{ height: spacing.sm }} />
              <AppButton
                title="Cancel"
                variant="secondary"
                onPress={() => setFormVisible(false)}
                disabled={formSaving}
              />
            </ScrollView>
          </Pressable>
        </KeyboardAvoidingView>
        {formVisible ? <OperationFeedbackHost /> : null}
      </Modal>
    );
  }

  function renderWeekOffForm() {
    const padded: (CalendarDay | null)[] = [];
    if (calendarData?.calendarDays?.length) {
      const offset = calendarData.calendarDays[0].dayOfWeek - 1;
      padded.push(...Array(offset).fill(null), ...calendarData.calendarDays);
    }
    const selectedCount = weekOffDates.length;
    return (
      <Modal
        visible={weekOffVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setWeekOffVisible(false)}
      >
        <Pressable style={themed.overlay} onPress={() => setWeekOffVisible(false)}>
          <Pressable onPress={() => {}} style={themed.sheet}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <Text style={themed.sheetTitle}>
                {weekOffMode === 'create' ? 'Add Weekly Off' : 'Edit Weekly Off'}
              </Text>
              <Text style={themed.weekOffHint}>
                {weekOffMode === 'create'
                  ? `Select today or a future date in ${MONTH_NAMES[month - 1]} ${year}, then Apply once.`
                  : `Choose today or a future date in ${MONTH_NAMES[month - 1]} ${year}.`}
              </Text>

              <View style={themed.weekOffWeekHeader}>
                {DAY_LABELS.map((label) => (
                  <View key={label} style={themed.weekOffCell}>
                    <Text style={themed.weekHeaderText}>{label}</Text>
                  </View>
                ))}
              </View>
              <View style={themed.weekOffGrid}>
                {padded.map((day, idx) => {
                  if (!day) {
                    return <View key={`empty-wo-${idx}`} style={themed.weekOffCell} />;
                  }
                  const selected = weekOffDates.includes(day.date);
                  const past = !isEditableCalendarDate(day.date, todayDate);
                  const taken =
                    !!day.holidayId &&
                    !(weekOffMode === 'edit' && day.holidayId === weekOffEditId);
                  const disabled = taken || past;
                  return (
                    <Pressable
                      key={day.date}
                      style={themed.weekOffCell}
                      disabled={disabled}
                      onPress={() => toggleWeekOffDate(day.date)}
                      accessibilityRole="button"
                      accessibilityState={{ selected, disabled }}
                      accessibilityLabel={
                        past
                          ? `${day.date}, past date cannot be edited`
                          : day.date
                      }
                    >
                      <View
                        style={[
                          themed.weekOffDay,
                          {
                            backgroundColor: selected
                              ? palette.primary
                              : 'transparent',
                            opacity: disabled ? 0.35 : 1,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            themed.weekOffDayText,
                            {
                              color: selected
                                ? palette.onPrimary
                                : disabled
                                  ? palette.textMuted
                                  : palette.textPrimary,
                            },
                          ]}
                        >
                          {Number(day.date.slice(8))}
                        </Text>
                      </View>
                    </Pressable>
                  );
                })}
              </View>

              <Text style={themed.selectedDatesText}>
                {selectedCount
                  ? `${selectedCount} date${selectedCount === 1 ? '' : 's'} selected: ${weekOffDates
                      .map(formatDisplayDate)
                      .join(', ')}`
                  : 'No dates selected'}
              </Text>

              {weekOffError ? (
                <Text style={themed.formError}>{weekOffError}</Text>
              ) : null}

              <AppButton
                title="Apply"
                onPress={applyWeekOffs}
                loading={weekOffSaving}
                disabled={weekOffSaving}
              />
              <View style={{ height: spacing.sm }} />
              <AppButton
                title="Cancel"
                variant="secondary"
                onPress={() => setWeekOffVisible(false)}
                disabled={weekOffSaving}
              />
            </ScrollView>
          </Pressable>
        </Pressable>
        {weekOffVisible ? <OperationFeedbackHost /> : null}
      </Modal>
    );
  }

  return (
    <PermissionGuard
      anyPermissions={[PERMISSIONS.ATTENDANCE_VIEW, PERMISSIONS.CALENDAR_VIEW]}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen
        scroll={false}
        webLayout="wide"
        contentStyle={isWeb ? webStyles.screen : undefined}
      >
        <ScreenHeader
          title={calendarTitle}
          onBack={navigation.canGoBack() ? () => navigation.goBack() : undefined}
          style={isWeb && !isCompact ? webStyles.header : undefined}
        />
        {canManage && holidayCalendarId == null ? (
          <View style={{ paddingHorizontal: spacing.md, paddingBottom: spacing.sm }}>
            <AppButton
              title="Manage calendars"
              variant="secondary"
              onPress={() =>
                (navigation as { navigate: (name: string) => void }).navigate(
                  routes.holidayCalendarList,
                )
              }
            />
          </View>
        ) : null}
        <ScrollView
          style={isWeb ? webStyles.scroll : undefined}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            { paddingBottom: spacing.xxl },
            isWeb ? webStyles.scrollContent : null,
          ]}
        >
        {/* Month navigation */}
        <View style={themed.monthNav}>
          <Pressable
            style={themed.monthNavBtn}
            onPress={prevMonth}
            accessibilityRole="button"
            accessibilityLabel="Previous month"
          >
            <Text style={themed.monthNavArrow}>‹</Text>
          </Pressable>
          <Text style={themed.monthLabel}>
            {MONTH_NAMES[month - 1]} {year}
          </Text>
          <Pressable
            style={themed.monthNavBtn}
            onPress={nextMonth}
            accessibilityRole="button"
            accessibilityLabel="Next month"
          >
            <Text style={themed.monthNavArrow}>›</Text>
          </Pressable>
        </View>

        {/* Weekday header */}
        <View style={themed.weekHeader}>
          {DAY_LABELS.map((d) => (
            <View key={d} style={themed.weekHeaderCell}>
              <Text style={themed.weekHeaderText}>{d}</Text>
            </View>
          ))}
        </View>

        {/* Calendar grid */}
        {error ? (
          <Text style={themed.errorText}>{error}</Text>
        ) : loading ? (
          <Text style={[themed.errorText, { color: palette.textMuted }]}>Loading…</Text>
        ) : (
          renderCalendarGrid()
        )}

        {!loading && !error ? renderLegend() : null}

        {/* Summary */}
        {!loading && !error ? renderSummary() : null}

        {/* Notes */}
        {!loading && !error ? renderNotes() : null}

        {/* Holidays list */}
        {!loading && !error ? renderHolidaysList() : null}

        {/* Add Holiday button (admin only) */}
        {canManage && !loading && !error && canAddInViewedMonth ? (
          <View style={themed.addHolidayBtn}>
            <AppButton title="+ Add Holiday" onPress={openCreateHolidayForm} />
            <AppButton
              title="+ Add Weekly Off"
              variant="secondary"
              onPress={openCreateWeekOffForm}
            />
          </View>
        ) : canManage && !loading && !error ? (
          <Text style={[themed.notesBody, { marginHorizontal: spacing.lg, marginTop: spacing.md }]}>
            Holiday and Weekly Off can only be added or edited for today or a future date.
          </Text>
        ) : null}

        {/* Selected day detail */}
        {renderSelectedDayDetail()}
      </ScrollView>

      {/* Holiday form modal */}
      {renderHolidayForm()}
      {renderWeekOffForm()}
      </AppScreen>
    </PermissionGuard>
  );
}

const webStyles = StyleSheet.create({
  /** Tablet+ web: the header is a standalone banner, so separate it from the content. */
  header: {
    marginBottom: spacing.md,
  },
  screen: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflow: 'scroll',
  },
  scrollContent: {
    flexGrow: 1,
  },
});
